import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { toPlain } from "@/lib/fieldCodec";
import { publishCms } from "@/services/cmsApi";
import { SectionCard } from "./SectionCard";
import { ObjectFields } from "./ObjectFields";
import { CmsPublishGate } from "./CmsPublishGate";
import { VersionsSheet } from "./VersionsSheet";
import { PoliciesEditor } from "./PoliciesEditor";
import { useDoc } from "@/hooks/useDoc";

const SKIP = ["id", "updated_at", "updated_by"];
// Set by the website code (site_content auto-sync); shown, not edited.
const META = ["page", "label", "path", "layout"];
const when = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const clean = (d) => Object.fromEntries(Object.entries(toPlain(d)).filter(([k]) => !SKIP.includes(k)));

// Customer Website CMS: every doc in site_content, every section/field/image, publish via password + OTP.
export function SiteContentEditor() {
  const docs = useCollection("site_content");
  const [docId, setDocId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [gate, setGate] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const live = docs.find((d) => d.id === docId);
  const liveClean = useMemo(() => (live ? clean(live) : null), [live]);
  const dirty = draft && liveClean && JSON.stringify(draft) !== JSON.stringify(liveClean);

  useEffect(() => { if (!docId && docs.length) setDocId(docs.find((d) => d.id === "home")?.id || docs[0].id); }, [docs, docId]);
  useEffect(() => { if (liveClean && !dirty) setDraft(liveClean); }, [liveClean]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = Array.isArray(draft?.sections) ? [...draft.sections].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) : null;
  const setSections = (list) => setDraft({ ...draft, sections: list.map((s, i) => ({ ...s, position: i + 1 })) });
  const move = (i, d) => { const a = [...sections]; [a[i], a[i + d]] = [a[i + d], a[i]]; setSections(a); };
  const [dragIdx, setDragIdx] = useState(null);
  const dropAt = (to) => { if (dragIdx == null || dragIdx === to) return; const a = [...sections]; const [m] = a.splice(dragIdx, 1); a.splice(to, 0, m); setSections(a); setDragIdx(null); };
  const { data: sys } = useDoc("system_status", "global");
  const hero = sections?.find((s) => s.name === "hero");
  const feeInNote = hero?.feeNote?.match(/₹\s?([\d,]+)/)?.[1]?.replace(/,/g, "");
  const feeMismatch = feeInNote && sys?.start_project_fee != null && Number(feeInNote) !== Number(sys.start_project_fee);
  // The website backend copies every page's content here and keeps it in step with the code.
  const { data: report } = useDoc("site_content_sync", "_report");
  const { data: syncState } = useDoc("site_content_sync", docId || "-");
  const order = ["home", "layout", ...(report?.pages || [])];
  const rank = (id) => (id === "policies" ? 999 : order.indexOf(id) === -1 ? 500 : order.indexOf(id));
  const docIds = Array.from(new Set([...docs.map((d) => d.id), "policies"])).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  const labelOf = (id) => docs.find((d) => d.id === id)?.label || report?.labels?.[id] || id;
  const fixed = draft?.layout === "fixed";
  const publish = () => setGate({
    title: `Publish site_content/${docId}`, description: "Goes live on the customer website immediately. Requires the profile password and an OTP sent to",
    run: async (auth) => { await publishCms({ ...auth, target: "site_content", doc_id: docId, data: draft }); toast.success("Website content published"); },
  });

  if (!docs.length) return <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="site-empty">Loading site_content…</Card>;
  return (
    <div data-testid="site-content-editor">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {docIds.map((id) => (
          <Button key={id} size="sm" variant={id === docId ? "default" : "outline"} className="h-8 font-mono" onClick={() => { if (!dirty || window.confirm("Discard unpublished changes?")) { setDocId(id); setDraft(null); } }} data-testid={`site-doc-${id}`} title={`site_content/${id}`}>{labelOf(id)}</Button>
        ))}
        {docId !== "policies" && <div className="ml-auto flex items-center gap-2">
          {dirty && <span className="text-xs text-amber-700 flex items-center gap-1" data-testid="site-dirty"><Icons.CircleDot className="h-3 w-3" /> Unpublished changes</span>}
          {dirty && <Button size="sm" variant="ghost" onClick={() => setDraft(liveClean)} data-testid="site-discard">Discard</Button>}
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowVersions(true)} data-testid="site-versions-btn"><Icons.History className="h-4 w-4" /> Versions</Button>
          <Button size="sm" className="gap-1.5" disabled={!dirty} onClick={publish} data-testid="site-publish-btn"><Icons.UploadCloud className="h-4 w-4" /> Publish</Button>
        </div>}
      </div>
      {report?.uncovered_routes?.length > 0 && (
        <p className="mb-3 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2" data-testid="site-uncovered">
          Website pages with no editable content yet: {report.uncovered_routes.join(", ")}. They appear here automatically once the website adds a content file for them.
        </p>
      )}
      {docId !== "policies" && draft && (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="site-doc-info">
          {draft.path && <span>Page: <span className="font-mono text-foreground">{draft.path === "*" ? "every page" : draft.path}</span></span>}
          <span className="font-mono">site_content/{docId}</span>
          {fixed && <span>Fixed design: edit the text and images; sections can't be moved or hidden.</span>}
          {syncState?.synced_at && <span data-testid="site-sync-info">Auto-synced from the website code {when(syncState.last_changed_at || syncState.synced_at)}{syncState.last_changes?.length ? ` (${syncState.last_changes.join("; ")})` : ""}</span>}
        </div>
      )}
      {docId === "policies" && <PoliciesEditor live={live || null} />}
      {feeMismatch && docId !== "policies" && (
        <p className="mb-3 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2" data-testid="fee-mismatch">
          hero.feeNote says ₹{feeInNote} but system_status/global.start_project_fee is ₹{sys.start_project_fee}. Keep them consistent.
        </p>
      )}
      {docId !== "policies" && draft && (sections ? (
        <div className="space-y-3">
          {sections.map((s, i) => (
            <SectionCard key={`${s.name}-${i}`} fixed={fixed} section={s} index={i} total={sections.length} onMove={(d) => move(i, d)} onDragStart={setDragIdx} onDropAt={dropAt} onChange={(v) => setSections(sections.map((x, j) => (j === i ? v : x)))} />
          ))}
          {Object.keys(draft).filter((k) => k !== "sections" && !META.includes(k)).length > 0 && (
            <Card className="p-4"><ObjectFields obj={draft} onChange={setDraft} path={docId} skip={["sections", ...META]} /></Card>
          )}
        </div>
      ) : <Card className="p-4"><ObjectFields obj={draft} onChange={setDraft} path={docId} /></Card>)}
      <CmsPublishGate gate={gate} onClose={() => setGate(null)} />
      <VersionsSheet target="site_content" docId={docId} open={showVersions} onClose={() => setShowVersions(false)} />
    </div>
  );
}
